import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { AtomBox, Button, Checkbox, Flex, Input, PreTitle, Text } from '@pulsex/uikit'
import { useTranslation } from '@pulsex/localization'
import { useActiveChainId } from 'hooks/useActiveChainId'
import {
  AUTO,
  getRpcOptions,
  getRpcPreference,
  setRpcPreference,
} from 'config/rpc'

type Latency = number | null | undefined // number = ms, null = unreachable, undefined = pending

async function pingRpc(url: string): Promise<number | null> {
  const controller = new AbortController()
  const timer = setTimeout(() => controller.abort(), 5_000)
  const start = performance.now()
  try {
    const res = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ jsonrpc: '2.0', method: 'eth_blockNumber', params: [], id: 1 }),
      signal: controller.signal,
    })
    if (!res.ok) return null
    await res.json()
    return Math.round(performance.now() - start)
  } catch {
    return null
  } finally {
    clearTimeout(timer)
  }
}

function useRpcLatencies(urls: string[]): Record<string, Latency> {
  const [latencies, setLatencies] = useState<Record<string, Latency>>({})
  const key = urls.join(',')
  const activeRef = useRef(true)

  useEffect(() => {
    activeRef.current = true
    const measure = () => {
      urls.forEach(async (url) => {
        const ms = await pingRpc(url)
        if (activeRef.current) setLatencies((prev) => ({ ...prev, [url]: ms }))
      })
    }
    measure()
    const interval = setInterval(measure, 10_000)
    return () => {
      activeRef.current = false
      clearInterval(interval)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key])

  return latencies
}

function LatencyBadge({ ms }: { ms: Latency }) {
  if (ms === undefined) return <Text color="textSubtle" fontSize="12px">…</Text>
  if (ms === null) return <Text color="failure" fontSize="12px">timeout</Text>
  const color = ms < 200 ? 'success' : ms < 500 ? 'warning' : 'failure'
  return (
    <Text color={color} fontSize="12px">
      <span
        style={{
          display: 'inline-block',
          width: 6,
          height: 6,
          borderRadius: '50%',
          background: 'currentColor',
          marginRight: 4,
        }}
      />
      {ms}ms
    </Text>
  )
}

function Row({
  label,
  checked,
  latency,
  onSelect,
}: {
  label: string
  checked: boolean
  latency?: Latency
  onSelect: () => void
}) {
  return (
    <Flex justifyContent="space-between" alignItems="center" mb="16px">
      <Flex as="label" alignItems="center" style={{ gap: '12px', cursor: 'pointer' }}>
        <Checkbox scale="sm" checked={checked} onChange={onSelect} />
        <Text>{label}</Text>
      </Flex>
      {latency !== undefined && <LatencyBadge ms={latency} />}
    </Flex>
  )
}

export default function RpcSettings() {
  const { t } = useTranslation()
  const { chainId } = useActiveChainId()
  const options = useMemo(() => getRpcOptions(chainId), [chainId])
  const pref = getRpcPreference(chainId)
  const selectedUrl = pref === AUTO ? null : pref
  const isCustom = selectedUrl !== null && !options.some((o) => o.url === selectedUrl)

  const customSaved = isCustom ? selectedUrl : null
  const optionUrls = useMemo(() => options.map((o) => o.url), [options])
  // Also ping the saved custom node so its row shows a latency.
  const pingUrls = useMemo(
    () => (customSaved ? [...optionUrls, customSaved] : optionUrls),
    [optionUrls, customSaved],
  )
  const latencies = useRpcLatencies(pingUrls)

  const [customUrl, setCustomUrl] = useState(isCustom ? selectedUrl! : '')
  const trimmedCustom = customUrl.trim()
  const isCustomValid = trimmedCustom === '' || /^https?:\/\/.+/i.test(trimmedCustom)

  const apply = useCallback(
    (value: string) => {
      if (value === getRpcPreference(chainId)) return
      setRpcPreference(chainId, value)
      window.location.reload()
    },
    [chainId],
  )

  return (
    <AtomBox mb="24px">
      <PreTitle mb="16px">{t('RPC Node')}</PreTitle>
      <Row label={t('Auto Mode')} checked={pref === AUTO} onSelect={() => apply(AUTO)} />
      {options.map((option) => (
        <Row
          key={option.url}
          label={option.label}
          checked={selectedUrl === option.url}
          latency={latencies[option.url]}
          onSelect={() => apply(option.url)}
        />
      ))}
      <Flex justifyContent="space-between" alignItems="center" mb="8px">
        <Flex as="label" alignItems="center" style={{ gap: '12px', cursor: 'pointer' }}>
          <Checkbox scale="sm" checked={isCustom} readOnly />
          <Text>{t('Custom RPC URL')}</Text>
        </Flex>
        {customSaved && latencies[customSaved] !== undefined && <LatencyBadge ms={latencies[customSaved]} />}
      </Flex>
      <Flex alignItems="center" style={{ gap: '8px' }}>
        <Input
          scale="sm"
          placeholder="https://"
          value={customUrl}
          isWarning={!isCustomValid}
          onChange={(e) => setCustomUrl(e.target.value)}
        />
        <Button
          scale="sm"
          disabled={!isCustomValid || trimmedCustom === '' || trimmedCustom === selectedUrl}
          onClick={() => apply(trimmedCustom)}
        >
          {t('Save')}
        </Button>
      </Flex>
      {!isCustomValid && (
        <Text color="failure" fontSize="12px" mt="4px">
          {t('Enter a valid http(s) URL')}
        </Text>
      )}
    </AtomBox>
  )
}
